import React, { useRef, useEffect, useState, useContext } from "react";
import { UserContext } from "../../context/user.context";
import { PhoneIcon } from "@heroicons/react/20/solid";
import {
  updateCustomerProfile,
  checkPhoneExists,
} from "../../utils/SonicAPIV2";
import { verifyOTP } from "../../utils/zulalWebAPI";
import { toast } from "react-toastify";

export const UserProfile1 = () => {
  const { currentUser, refreshUserData } = useContext(UserContext);

  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const [user, setUser] = useState({});
  const [confirmOTP, setConfirmOTP] = useState("");
  const [mobileUpdate, setMobileUpdate] = useState(false);
  const [btnOTPText, setbtnOTPText] = useState("Send OTP");
  const [messageText, setMessageText] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false); // ✅ Track initial load

  const onChangeHandler = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });

    if (e.target.name === "phoneNo") {
      setMessageText(
        "Note: You need to verify your Mobile Number by sending and entering the OTP."
      );
      setMobileUpdate(true);
      setbtnOTPText("Send OTP"); // ✅ Reset OTP button when phone changes
    }
  };

  function getLastNineDigits(mobileNumber) {
    if (mobileNumber == null) return "";
    const mobileStr = mobileNumber.toString();
    if (mobileStr.length >= 9) {
      return mobileStr.slice(-9);
    } else {
      return mobileStr;
    }
  }

  // ✅ Now watches all relevant fields from currentUser
  useEffect(() => {
    if (!currentUser?.customerCode) return;

    setUser({
      outletName: currentUser.outletName || "",
      phoneNo: getLastNineDigits(currentUser.phoneNo) || "",
      email: currentUser.email || "",
      address: currentUser.address || "",
    });
  }, [
    currentUser?.customerCode,
    currentUser?.outletName,
    currentUser?.phoneNo,
    currentUser?.email,
    currentUser?.address,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mobileUpdate) {
      setMessageText("Please verify your Mobile Number before updating!");
      toast.warning("Please verify your Mobile Number before updating!");
      return;
    }

    // ✅ Validation
    if (!user.outletName || user.outletName.trim() === "") {
      toast.error("Name cannot be empty");
      return;
    }

    if (!user.email || user.email.trim() === "") {
      toast.error("Email cannot be empty");
      return;
    }

    setIsUpdating(true);

    // console.log("Submitting profile update with data:", user);

    const userUpdateDetails = {
      outletName: user.outletName.trim(),
      email: user.email.trim(),
      phoneNumber: user.phoneNo,
      address:
        currentUser.customerDetails?.user?.customerDetails?.address || "N/A",
    };

    // Transform to the required format
    const updateData = {
      name: userUpdateDetails.outletName,
      email: userUpdateDetails.email,
      numbers: [
        {
          number: `971${userUpdateDetails.phoneNumber}`,
          is_Default: true,
          countryId: 1,
        },
      ],
    };

    try {
      const updateCustRes = await updateCustomerProfile(updateData);
      // console.log("Profile Update Response:", updateCustRes);

      if (updateCustRes.success) {
        toast.success("Profile Updated Successfully!");
        setMessageText("Profile Updated Successfully!");

        // ✅ Refresh user data from the server
        setInitialLoadDone(false); // ✅ Allow reload after refresh
        await refreshUserData();

        // console.log("User data refreshed from server");
      } else {
        setMessageText("Profile Update Failed!");
        toast.error(updateCustRes.message || "Profile Update Failed!");
      }
    } catch (error) {
      // console.error("Error updating profile:", error);
      setMessageText("Error updating profile: " + error.message);
      toast.error("Error updating profile: " + error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const sendOTP = async (e) => {
    e.preventDefault();

    try {
      if (btnOTPText === "Send OTP") {
        if (!user.phoneNo || user.phoneNo.length < 9) {
          toast.error("Please enter a valid 9-digit phone number");
          return;
        }

        // ✅ Check if phone number changed
        const originalPhone = getLastNineDigits(currentUser.phoneNo);
        if (user.phoneNo === originalPhone) {
          toast.info("Phone number hasn't changed");
          setMobileUpdate(false);
          setMessageText("");
          return;
        }

        const checkPhone = await checkPhoneExists(user.phoneNo);
        // console.log("Phone check result:", checkPhone);

        if (checkPhone.exists) {
          toast.error("Phone number already registered. Please use another.");
          return;
        }

        toast.success(
          "Phone number available. Please check your phone for the OTP."
        );

        setbtnOTPText("Verify OTP");
      } else if (btnOTPText === "Verify OTP") {
        if (!confirmOTP || confirmOTP.trim() === "") {
          toast.error("Please enter the OTP");
          return;
        }

        // console.log("Verifying OTP:", confirmOTP);

        const verifyRes = await verifyOTP(user.phoneNo, confirmOTP);

        if (verifyRes.success) {
          toast.success("OTP Verified! You may update the profile now.");
          setMobileUpdate(false);
          setbtnOTPText("OTP Verified");
          setMessageText("OTP Verified! You can now update your profile.");
        } else {
          toast.error("OTP verification failed!");
        }
      }
    } catch (err) {
      // console.error("OTP error:", err);
      toast.error(`Error: ${err.apiMessage || err.message}`);
    }
  };

  return (
    <div>
      <div className="mx-auto mr-4 ml-4 mb-28">
        <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
          Profile
          <p className="text-darkTextColor font-poppins text-C12">
            Update your profile including Full Name, Email and Phone Number
          </p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
            <div>
              <label
                htmlFor="customerCode"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Customer Code
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  value={currentUser.customerCode || ""}
                  readOnly
                  name="customerCode"
                  id="customerCode"
                  placeholder="Customer Code"
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
     placeholder:text-gray-400 bg-gray-100 cursor-not-allowed sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="outletName"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Full Name
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  value={user.outletName || ""}
                  onChange={onChangeHandler}
                  name="outletName"
                  id="outletName"
                  autoComplete="given-name"
                  placeholder="Full Name"
                  required
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                         placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Email
              </label>
              <div className="mt-2.5">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={user.email || ""}
                  onChange={onChangeHandler}
                  autoComplete="email"
                  required
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                         placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="phoneNo"
                className="block text-sm font-semibold leading-6 text-gray-900"
              >
                Phone number
              </label>
              <div className="flex flex-row mt-2.5">
                <span className="bg-darkVeryLight inline-flex items-center rounded-l-md border border-r-0 ring-1 ring-inset border-secondaryHover px-3 py-2 text-gray-500 sm:text-sm">
                  <PhoneIcon className="text-darkTextColor w-6" />
                  <div className="flex font-normal text-P14 w-10 items-end justify-end">
                    +971
                  </div>
                </span>
                <input
                  type="tel"
                  pattern="[0-9]{9}"
                  name="phoneNo"
                  id="phoneNo"
                  required
                  maxLength="9"
                  value={user.phoneNo || ""}
                  onChange={onChangeHandler}
                  autoComplete="tel"
                  className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 
                  focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6
                  invalid:border-red-500 invalid:text-red-600 focus:invalid:border-red-500 focus:invalid:ring-red-500"
                />
              </div>
            </div>

            {mobileUpdate && (
              <div className="grid grid-flow-col md:col-span-2 gap-4">
                <div>
                  <label
                    htmlFor="otp"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Confirm OTP
                  </label>
                  <div className="mt-2.5">
                    <input
                      type="text"
                      name="otp"
                      id="otp"
                      maxLength="6"
                      value={confirmOTP}
                      onChange={(e) => setConfirmOTP(e.target.value)}
                      placeholder="Enter 6-digit OTP"
                      className="block w-full max-w-xs rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-secondaryHover
                         placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="btnOTP"
                    className="block text-sm font-semibold leading-6 text-gray-900"
                  >
                    Update Mobile
                    <span className="text-darkTextColor font-poppins text-xs font-normal ml-2">
                      (Send & verify OTP)
                    </span>
                  </label>
                  <div className="mt-2.5">
                    <button
                      type="button"
                      onClick={sendOTP}
                      disabled={btnOTPText === "OTP Verified"}
                      className="rounded-md bg-secondary px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-secondaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:bg-green-600 disabled:cursor-not-allowed"
                    >
                      {btnOTPText}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={isUpdating}
              className="rounded-md bg-secondary disabled:bg-gray-400 disabled:cursor-not-allowed px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-secondaryHover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <span className="inline-flex items-center gap-2">
                {isUpdating && (
                  <span
                    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"
                    aria-label="Loading"
                  />
                )}
                {isUpdating ? "Updating..." : "Update Profile"}
              </span>
            </button>
          </div>
          {messageText && (
            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-gray-700">{messageText}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
